/**
 * Speakeasy "Notify me when back in stock" modal.
 * Markup shell: snippets/speakeasy-notify.liquid (#Speakeasy-Notify).
 * Styles: assets/speakeasy-notify.css.
 * Config: window.SpeakeasyNotify.config = { endpoint, success, email } (set by the shell).
 *
 * Delegated document click target (rendered by sections/main-product.liquid on
 * sold-out products, and any card that opts in):
 *   .spk-notify-trigger[data-product-title][data-product-handle] -> open modal
 *
 * On submit, POSTs JSON { email, product_title, product_handle, variant_id } to
 * config.endpoint. That serverless function (notify-backend/) holds the Shopify
 * Admin API token and appends the tag  wishlist-<product title>  to the customer,
 * creating the customer first if they don't exist yet.
 *
 * Reuses Dawn's global focus helpers (window.trapFocus / window.removeTrapFocus)
 * and the body 'overflow-hidden' scroll-lock class, plus window.Speakeasy.toast.
 * Never throws uncaught: every submit path resolves to success, the inline error,
 * or a toast.
 */

(function () {
  'use strict';

  if (window.SpeakeasyNotifyInit) return;
  window.SpeakeasyNotifyInit = true;

  var TRIGGER_SELECTOR = '.spk-notify-trigger[data-product-title]';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  window.SpeakeasyNotify = window.SpeakeasyNotify || {};
  var config = window.SpeakeasyNotify.config || {};

  var root = document.getElementById('Speakeasy-Notify');
  var dialog = root ? root.querySelector('.spk-notify__dialog') : null;
  var els = root
    ? {
        productName: root.querySelector('.spk-notify__product'),
        form: root.querySelector('.spk-notify__form'),
        input: root.querySelector('.spk-notify__input'),
        error: root.querySelector('.spk-notify__error'),
        submit: root.querySelector('.spk-notify__submit'),
        submitLabel: root.querySelector('.spk-notify__submit-label'),
        submitBusy: root.querySelector('.spk-notify__submit-busy'),
        success: root.querySelector('.spk-notify__success'),
        successText: root.querySelector('.spk-notify__success-text')
      }
    : null;

  var openedBy = null;
  var state = { product: null, submitting: false };

  var strings = {
    genericError: 'Something went wrong — please try again.',
    invalidEmail: 'Please enter a valid email address.',
    notConfigured: 'Restock alerts aren’t set up yet. Please check back soon.'
  };

  /* --------------------------------- utilities -------------------------------- */

  function toast(message) {
    if (!message) return;
    try {
      if (window.Speakeasy && typeof window.Speakeasy.toast === 'function') {
        window.Speakeasy.toast(message);
        return;
      }
      document.dispatchEvent(new CustomEvent('speakeasy:toast', { detail: { message: message } }));
    } catch (error) {
      /* toast is best-effort only */
    }
  }

  function showError(message) {
    if (!els.error) return;
    els.error.textContent = message;
    els.error.hidden = false;
    if (els.input) els.input.setAttribute('aria-invalid', 'true');
  }

  function clearError() {
    if (!els.error) return;
    els.error.hidden = true;
    els.error.textContent = '';
    if (els.input) els.input.removeAttribute('aria-invalid');
  }

  function setBusy(busy) {
    state.submitting = busy;
    if (!els.submit) return;
    els.submit.disabled = busy;
    if (busy) els.submit.setAttribute('aria-busy', 'true');
    else els.submit.removeAttribute('aria-busy');
    if (els.submitLabel) els.submitLabel.hidden = busy;
    if (els.submitBusy) els.submitBusy.hidden = !busy;
  }

  /* --------------------------------- modal shell ------------------------------ */

  function openModal(trigger) {
    openedBy = trigger;
    state.product = {
      title: trigger.getAttribute('data-product-title') || '',
      handle: trigger.getAttribute('data-product-handle') || '',
      variantId: trigger.getAttribute('data-variant-id') || ''
    };

    /* reset to the form state every open */
    clearError();
    setBusy(false);
    if (els.form) els.form.hidden = false;
    if (els.success) els.success.hidden = true;
    if (els.productName) {
      els.productName.textContent = state.product.title || 'this piece';
    }
    if (els.input) {
      els.input.value = config.email || els.input.value || '';
    }

    root.hidden = false;
    root.classList.remove('active');
    void root.offsetWidth; /* reflow so the pop-in animation restarts every open */
    root.classList.add('active');
    document.body.classList.add('overflow-hidden');

    if (typeof window.trapFocus === 'function') {
      try {
        window.trapFocus(dialog, els.input || dialog);
      } catch (error) {
        dialog.focus();
      }
    } else {
      (els.input || dialog).focus();
    }
  }

  function closeModal() {
    if (!root || root.hidden) return;
    root.classList.remove('active');
    root.hidden = true;
    document.body.classList.remove('overflow-hidden');
    var focusTarget = openedBy;
    openedBy = null;
    if (typeof window.removeTrapFocus === 'function') {
      try {
        window.removeTrapFocus(focusTarget);
      } catch (error) {
        /* focus restore is best-effort */
      }
    } else if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
  }

  function showSuccess() {
    var message = config.success || 'You’re on the list — we’ll email you when it returns.';
    if (els.successText) els.successText.textContent = message;
    if (els.form) els.form.hidden = true;
    if (els.success) els.success.hidden = false;
    toast(message);
  }

  /* ----------------------------------- submit --------------------------------- */

  function submitRequest(email) {
    var endpoint = config.endpoint || '';
    if (!endpoint) {
      return Promise.reject(new Error(strings.notConfigured));
    }
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        email: email,
        product_title: state.product.title,
        product_handle: state.product.handle,
        variant_id: state.product.variantId
      })
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok || (data && data.ok === false)) {
            var error = new Error('Notify request failed');
            error.description = (data && (data.error || data.message)) || '';
            throw error;
          }
          return data;
        });
    });
  }

  function onSubmit(event) {
    event.preventDefault();
    if (state.submitting) return;

    var email = (els.input && els.input.value ? els.input.value : '').trim();
    if (!EMAIL_RE.test(email)) {
      showError(strings.invalidEmail);
      if (els.input) els.input.focus();
      return;
    }

    clearError();
    setBusy(true);

    submitRequest(email)
      .then(function () {
        setBusy(false);
        showSuccess();
      })
      .catch(function (error) {
        setBusy(false);
        var message = (error && (error.description || error.message)) || strings.genericError;
        showError(message);
      });
  }

  /* ---------------------------------- handlers -------------------------------- */

  function onKeyup(event) {
    var code = String(event.code || event.key || '').toUpperCase();
    if (code === 'ESCAPE') closeModal();
  }

  function onInput() {
    if (els.error && !els.error.hidden) clearError();
  }

  function onDocumentClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    if (root && !root.hidden) {
      var closer = target.closest('[data-spk-notify-close]');
      if (closer && root.contains(closer)) {
        event.preventDefault();
        closeModal();
        return;
      }
    }

    var trigger = target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      if (!root || !dialog) return; /* no shell: leave the trigger inert */
      event.preventDefault();
      openModal(trigger);
    }
  }

  /* ------------------------------------ init ---------------------------------- */

  document.addEventListener('click', onDocumentClick);

  if (root && dialog && els) {
    root.addEventListener('keyup', onKeyup);
    if (els.form) els.form.addEventListener('submit', onSubmit);
    if (els.input) els.input.addEventListener('input', onInput);
  }
})();
